import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { SketchPicker as ColorPicker, ColorResult } from 'react-color'
import { Button, Form, Icon, Popup } from 'semantic-ui-react'
import DatepickerComponent from '../../../../components/Datepicker/Datepicker'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { _PHASECOLORS } from '../../../../constants/colors'
import { getColor } from '../../../../utils/getColor'
import { IProjectPhase } from '../../types/project'
import { IWizardProject } from '../../types/wizardData'

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
}

const maxLength20ValidationRule = new RegExp("^.{0,20}$")
const invalidMaxLength20ValidationRuleMsg = 'Max number of characters exceeded. There are only 20 chars allowed.'
const maxLength21 = 21

const WizardSelectPhase = (props: Props): JSX.Element => {
  const {
    open,
    data,
    wizardFinishedSteps,
    setWizardFinishedSteps,
    setWizardStep,
    setProjectWizardData,
    onClose
  } = props
  const [phaseList, setPhaseList] = useState<IProjectPhase[]>(data.phases.length !== 0 ? data.phases : [{ id: '', name: '', start_date: '', end_date: '', color: '', active: false, finished: false }])
  const [_open, setOpen] = useState(false)
  const [dateId, setDateId] = useState('')
  const [activities, setActivities] = useState(data.activities)
  const [removePhases, setRemovePhases] = useState<number[]>([])
  const [validTimeline, setValidTimeline] = useState(true)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [inputFocusedId, setInputFocusedId] = useState<string>('')

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.onscroll = (() => {
        setOpen(false)
        setDateId('')
      })
    }
  }, [])

  useEffect(() => {
    if (wizardFinishedSteps.includes(4)) {
      if (!(phaseList.length > 0 && phaseList.every(p =>
        p.name !== '' &&
        p.start_date &&
        p.end_date &&
        moment(p.start_date).isSameOrAfter(moment("01.01.2000", "DD.MM.YYYY")) &&
        moment(p.start_date).isSameOrBefore(moment(p.end_date)) &&
        p.name.length < maxLength21))) {
        setWizardFinishedSteps([1, 2, 3])
      }
    }
  }, [setWizardFinishedSteps, wizardFinishedSteps, phaseList])

  useEffect(() => {
    if (phaseList.length > 3) {
      const phaseFormElement = document.getElementById('phase' + (phaseList.length - 1).toString())
      if (phaseFormElement !== null) {
        phaseFormElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [phaseList.length])

  useEffect(() => {
    setOpen(true)
  }, [dateId])

  const removePhase = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, idx: number) => {
    const list = phaseList.filter((item, i) => i !== idx)
    const removedPhase = phaseList.filter((item, i) => i === idx)[0]
    setRemovePhases([...removePhases, +removedPhase.id])
    setPhaseList(list)
  }

  const goToNextStep = () => {
    const _phases = phaseList.filter(p => p.name !== '').map((phase, idx) => { return { ...phase, id: idx + 1 } })
    let _acts = activities
    if (removePhases.length !== 0) {
      _acts = _acts.map(a => {
        if (removePhases.includes(+a.phase.id)) {
          a.phase = { id: '', name: '', start_date: '', end_date: '', color: '', active: false, finished: false }
        } else {
          _phases.forEach(p => {
            if (p.name === a.phase.name) {
              a.phase = p
            }
          })
        }
        return a
      })
    }
    const _data: IWizardProject = {
      ...data,
      phases: _phases,
      activities: _acts
    }
    setRemovePhases([])
    if (!wizardFinishedSteps.includes(4)) {
      setWizardFinishedSteps([1, 2, 3, 4])
    }
    setWizardStep('step-four')
    setProjectWizardData(_data)
  }

  const goToPreviousStep = () => {
    const _data: IWizardProject = {
      ...data,
      phases: phaseList
    }
    setWizardStep('step-two')
    setProjectWizardData(_data)
  }

  const addPhase = () => {
    setPhaseList([...phaseList, { id: '', name: '', start_date: '', end_date: '', color: '', active: false, finished: false }])
  }

  const inputChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    let _activities = activities
    const _phases = phaseList.map((p, idx) => {
      if (idx === index) {
        if (p.color === '') {
          p.color = getColor(_PHASECOLORS, idx)
        }
        _activities = activities.map(a => {
          if (a.phase.id !== '' && a.phase.name === p.name) {
            a.phase.name = event.target.value
          }
          return a
        })
        p.name = event.target.value
      }
      return p
    })
    setPhaseList(_phases)
    setActivities(_activities)
  }

  const changeDates = (date: Date, index: number, indicator: 'start' | 'end') => {
    setPhaseList(phaseList.map((i, idx) => {
      if (idx === index) {
        if (indicator === 'start') {
          i.start_date = date
          i.end_date = ''
        } else if (indicator === 'end') {
          i.end_date = date
        }
      }
      return i
    }))
    const _data: IWizardProject = {
      ...data,
      phases: phaseList
    }
    setOpen(false)
    setProjectWizardData(_data)
  }

  const handleEnterButtonPress = (event: React.KeyboardEvent) => {
    const key = event.key
    key === 'Enter' ? event.preventDefault() : undefined
  }

  const onInputClick = (id: string) => {
    setDateId(id)
    if (_open) {
      setOpen(false)
    } else {
      setOpen(true)
    }
  }

  const renderDatepicker = (item: IProjectPhase, idx: number) => {
    return (
      <Form.Field width={7} style={{ paddingRight: 0, paddingLeft: '13px' }}>
        {renderLabels('Timelines', 2, idx)}
        {renderTimelinesContent(item, idx)}
      </Form.Field>
    )
  }

  const renderTimelinesContent = (item: IProjectPhase, idx: number) => {
    return (
      <div style={{ display: 'flex', marginTop: idx === 0 ? '2rem' : '', alignItems: 'center' }}>
        <DatepickerComponent
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd.MM.yyyy'
          inputWidth='6.5rem'
          inputBorderSmall={false}
          open={`${idx}-start-date` === dateId && _open}
          onClickOutside={() => setOpen(false)}
          onInputClick={() => onInputClick(`${idx}-start-date`)}
          onChange={(date: Date) => changeDates(date, idx, 'start')}
          selected={item.start_date ? moment(item.start_date).toDate() : undefined}
          placeholder='Start Date'
          validation
          returnOnValidation={isValid => setValidTimeline(isValid)}
        />
        <span style={{ padding: '0 6px' }}>-</span>
        <DatepickerComponent
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd.MM.yyyy'
          inputWidth='6.5rem'
          inputBorderSmall={false}
          open={`${idx}-end-date` === dateId && _open}
          onClickOutside={() => setOpen(false)}
          onInputClick={() => onInputClick(`${idx}-end-date`)}
          onChange={(date: Date) => changeDates(date, idx, 'end')}
          selected={item.end_date ? moment(item.end_date).toDate() : undefined}
          placeholder='End Date'
          maxDate={item.start_date ? new Date(moment(item.start_date).add(10, 'y').format('YYYY-MM-DD')) : undefined}
          minDate={item.start_date ? new Date(moment(item.start_date).add(1, 'days').format('YYYY-MM-DD')) : undefined}
          validation
          returnOnValidation={isValid => setValidTimeline(isValid)}
        />
      </div>
    )
  }

  const renderRemoveIcon = (idx: number) => {
    return (
      <Button disabled={phaseList.length < 2} key={idx} icon onClick={e => removePhase(e, idx)} style={{ backgroundColor: 'transparent', boxShadow: 'none', marginTop: idx === 0 ? '2.2rem' : '' }}>
        <Icon name='times circle' color='red' style={{ fontSize: '15px' }} />
      </Button>
    )
  }

  const renderPopup = (p: IProjectPhase, idx: number) => {
    return (
      <Popup
        basic
        style={{ marginLeft: '-1.5rem' }}
        trigger={
          <Icon
            name='square'
            size='large'
            style={{ zIndex: 1, color: p.color !== '' ? p.color : 'silver', position: 'relative', marginTop: idx === 0 ? '2.6rem' : '.6rem', marginLeft: '-2rem', paddingRight: '2rem' }}
            onClick={onClickColor}
          />}
        content={renderColorPicker(idx)}
        on='click'
        position='bottom left'
        hideOnScroll
      />
    )
  }

  const renderForm = () => {
    return phaseList.map((i, idx) => {
      return (
        <Form.Group id={'phase' + idx.toString()} key={idx} onKeyPress={handleEnterButtonPress}>
          <ValidationInput
            fluid
            value={i.name}
            onChange={e => inputChange(e, idx)}
            placeholder='Enter Phase'
            fieldWidth={9}
            label={renderLabels('Phase', 1, idx)}
            paddingRight='0'
            marginTop={idx === 0 ? '2rem' : ''}
            zIndex={1}
            validatePattern={maxLength20ValidationRule}
            invalidMessage={invalidMaxLength20ValidationRuleMsg}
            maxLength={maxLength21}
            
          />
          {renderPopup(i, idx)}
          {renderDatepicker(i, idx)}
          {renderRemoveIcon(idx)}
        </Form.Group>
      )
    })
  }

  const onClickColor = () => {
    setOpen(false)
  }

  const changeColor = (color: ColorResult['hex'], idx: number) => {
    const _phases = phaseList.map((p, i) => {
      if (i === idx) {
        p.color = color
      }
      return p
    })
    setPhaseList(_phases)
  }

  const renderColorPicker = (idx: number) => {
    return (
      <ColorPicker disableAlpha presetColors={_PHASECOLORS} color={phaseList[idx].color} onChange={color => changeColor(color.hex, idx)} />
    )
  }

  const disableNext = () => {
    return !(
      phaseList.length > 0 && phaseList.every(p =>
        p.name !== '' &&
        validTimeline &&
        p.start_date &&
        p.end_date &&
        moment(p.start_date).isSameOrAfter(moment("01.01.2000", "DD.MM.YYYY")) &&
        moment(p.start_date).isSameOrBefore(moment(p.end_date)) &&
        p.name.length < maxLength21
      )
    )
  }

  const renderLabels = (label: string, key: number, idx: number) => {
    if (idx === 0) {
      return (
        <label key={key} style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: 2, width: 'inherit', backgroundColor: 'white' }}>
          <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>{label}</p>
        </label>
      )
    }
    return null
  }

  const renderContent = () => {
    return (
      <div>
        <div ref={scrollRef} onScroll={() => setInputFocusedId('')} id='scrollable' style={{ overflowY: phaseList.length > 3 ? 'scroll' : 'hidden', overflowX: 'hidden', maxHeight: '13rem', scrollbarWidth: 'thin' }}>
          <Form style={{ position: 'inherit' }}>{renderForm()}</Form>
        </div>
        <Button
          className='button-add-item'
          inverted
          size='small'
          color='teal'
          onClick={() => addPhase()}
          content='Add Phase'
          icon={<Icon name='plus' />}
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: '0rem', color: '#08D6A1', backgroundColor: '#CDF7EC' }}
        />
      </div>
    )
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPreviousStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, color: '#747678', borderColor: '#BEC8C3', marginRight: '1rem' }}
        />
        <Button
          primary={true}
          disabled={disableNext()}
          size='small'
          onClick={goToNextStep}
          content='Next'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...data,
      phases: phaseList
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' }
    ]
    return (
      <WizardComponent
        open={open}
        close={onClose}
        content={renderContent()}
        headerContent='Define Phases and Timelines'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={data.type === 'service_provider' ? 6 : 5}
        activeStep={3}
        width={'670px'}
        finishedSteps={wizardFinishedSteps}
        setWizardStep={setWizardStep}
        setProjectWizardData={setProjectWizardData}
        data={_data}
        stepStringList={_steps}
        headerContentFontSize='31.5px'
      />
    )
  }

  return renderWizard()
}

export default WizardSelectPhase
